import cn from 'classnames';
import { forwardRef } from 'react';

import type { ForwardedRef } from 'react';

import type { BaseFieldProps } from './base-field.types';

import './base-field.scss';

/**
 * @internal
 * Base component provides the layout for the field which includes:
 * label (with or without tooltip), helper text and control.
 * It also provides the `error` and `full width` states for the field.
 * Should be used as a base for all other field components and should not be exposed to the end users.
 */
const BaseField = forwardRef((props: BaseFieldProps, ref: ForwardedRef<HTMLDivElement>) => {
  const {
    className,
    control,
    error,
    fullWidth,
    helperText,
    label,
    labelPosition = 'block-start',
    metadata,
    required: _required, // Destructured but not used - required indicator is now handled by FieldLabel
    tooltip: _tooltip, // Destructured but not used - tooltip is now handled by FieldLabel
    ...rest
  } = props;

  const hasLabel = Boolean(label || metadata);
  const hasHelper = Boolean(helperText);

  return (
    <div
      {...rest}
      className={cn('rbui-base-field', className, {
        'rbui-base-field--error': error,
        'rbui-base-field--full-width': fullWidth,
        'rbui-base-field--has-helper': hasHelper,
        'rbui-base-field--label-block-start': hasLabel && labelPosition === 'block-start',
        'rbui-base-field--label-inline-end': hasLabel && labelPosition === 'inline-end',
        'rbui-base-field--label-inline-start': hasLabel && labelPosition === 'inline-start',
      })}
      ref={ref}
    >
      {hasLabel && (
        <div className={cn('rbui-base-field__label')}>
          {label}
          {metadata && <span className="rbui-base-field__metadata">{metadata}</span>}
        </div>
      )}
      <div className="rbui-base-field__control">{control}</div>
      {hasHelper && (
        <span className="rbui-base-field__helper">{helperText === ' ' ? <span>&nbsp;</span> : helperText}</span>
      )}
    </div>
  );
});

BaseField.displayName = 'BaseField';

export default BaseField;
